import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from '../config';
import { formatDate, formatDateTime } from '../utils/datetime';

export default function LookupSuggest({
  datasetId,
  mode = 'select',
  value,
  onSelect,
  placeholder,
  type = 'text',
  inputClassName,
  searchOnChange = true,
  onChangeText
}) {
  const [q, setQ] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [warned, setWarned] = useState(false);
  const timeoutRef = useRef();
  const inputRef = useRef(null);
  const portalRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setQ(prev => (prev === (value || '') ? prev : (value || '')));
  }, [value]);

  const runSearch = useCallback((text) => {
    if (!datasetId) return;
    if (!text || !text.trim()) {
      setItems([]); setOpen(false); setLoading(false); return;
    }
    clearTimeout(timeoutRef.current);
    setLoading(true);
    timeoutRef.current = setTimeout(() => {
      const token = localStorage.getItem('token');
      fetch(`${API_BASE_URL}/api/lookup/datasets/${encodeURIComponent(datasetId)}/entries?q=${encodeURIComponent(text)}&limit=15`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => {
          setItems(data || []);
          setOpen((data || []).length > 0);
          setSelectedIndex(-1);
          if (mode === 'warn') setWarned((data || []).length > 0);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 280);
  }, [datasetId, mode]);

  useEffect(() => {
    if (!searchOnChange) return;
    runSearch(q);
    return () => clearTimeout(timeoutRef.current);
  }, [q, runSearch, searchOnChange]);

  useEffect(() => {
    portalRef.current = document.createElement('div');
    portalRef.current.className = 'lookup-suggest-portal';
    document.body.appendChild(portalRef.current);
    return () => {
      if (portalRef.current) {
        try { document.body.removeChild(portalRef.current); } catch (e) {}
        portalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    function updatePos() {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownStyle({
        left: rect.left + window.scrollX + 'px',
        top: rect.bottom + window.scrollY + 4 + 'px',
        width: Math.max(rect.width, 320) + 'px'
      });
    }
    if (open) {
      updatePos();
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true);
    }
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      const portalEl = portalRef.current;
      const wrapperEl = inputRef.current && inputRef.current.closest('.lookup-suggest');
      if (portalEl && wrapperEl) {
        if (portalEl.contains(e.target) || wrapperEl.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (it) => {
    if (mode === 'select') {
      const displayText = it.label || (it.value != null ? String(it.value) : '');
      setQ(displayText);
      onSelect && onSelect(it);
    } else {
      onSelect && onSelect(it);
    }
    setOpen(false);
    setSelectedIndex(-1);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQ(v);
    setOpen(true);
    if (mode === 'warn' && !v) setWarned(false);
    onChangeText && onChangeText(v);
  };

  return (
    <div className="lookup-suggest" style={{ position: 'relative', width: '100%' }}>
      <input
        ref={inputRef}
        type={type}
        placeholder={placeholder || 'Type to search…'}
        value={q}
        onChange={handleChange}
        onFocus={() => { if (items.length) setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (items.length) setSelectedIndex(i => (i < items.length - 1 ? i + 1 : 0));
            setOpen(true);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (items.length) setSelectedIndex(i => (i > 0 ? i - 1 : items.length - 1));
          } else if (e.key === 'Enter') {
            if (selectedIndex >= 0 && items[selectedIndex]) {
              e.preventDefault();
              pick(items[selectedIndex]);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
            setSelectedIndex(-1);
          }
        }}
        className={inputClassName || 'form-field-input'}
        autoComplete="off"
      />
      {loading && (
        <div style={{ position: 'absolute', right: 8, top: 10, fontSize: 12, color: '#64748b' }}>…</div>
      )}

      {mode === 'warn' && warned && items.length > 0 && (
        <div style={{
          marginTop: 6, padding: '10px 12px', border: '1px solid #fcd34d',
          background: '#fffbeb', borderRadius: 6, fontSize: 13, color: '#92400e'
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {items.length} existing match{items.length === 1 ? '' : 'es'} found — review before submitting:
          </div>
          <div style={{ maxHeight: 220, overflow: 'auto' }}>
            {items.slice(0, 5).map(it => (
              <div key={it.id} style={{ padding: '8px 0', borderTop: '1px solid #fde68a' }}>
                <div style={{ color: '#0f172a', fontWeight: 600 }}>{it.label || it.value}</div>
                <MetadataLine items={it.metadata} />
              </div>
            ))}
          </div>
        </div>
      )}

      {(open && items && items.length > 0 && mode === 'select') && portalRef.current && ReactDOM.createPortal(
        <div className="lookup-suggest-list" style={{
          position: 'absolute', ...dropdownStyle, zIndex: 99999,
          maxHeight: 320, overflow: 'auto', background: 'white',
          border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          {items.map((it, i) => (
            <div
              key={it.id}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              style={{
                padding: '8px 12px', cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9',
                background: i === selectedIndex ? '#eff6ff' : 'transparent'
              }}
            >
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{it.label || it.value}</div>
              <MetadataLine items={it.metadata} />
            </div>
          ))}
        </div>,
        portalRef.current
      )}
    </div>
  );
}

export function MetadataLine({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ fontSize: 12, color: '#475569', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {items.map((m, i) => (
        <span key={i}>
          <span style={{ color: '#64748b' }}>{m.label}:</span>{' '}
          <span style={{ color: '#0f172a' }}>{formatValue(m.value, m.format)}</span>
        </span>
      ))}
    </div>
  );
}

export function MetadataCard({ items, title }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{
      marginTop: 8, padding: '10px 12px',
      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6
    }}>
      {title && <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>{title}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 13 }}>
        {items.map((m, i) => (
          <React.Fragment key={i}>
            <div style={{ color: '#64748b' }}>{m.label}</div>
            <div style={{ color: '#0f172a', fontWeight: 500 }}>{formatValue(m.value, m.format)}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function formatValue(value, format) {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'date') {
    const formatted = formatDate(value);
    if (formatted) return formatted;
  }
  if (format === 'datetime') {
    const formatted = formatDateTime(value);
    if (formatted) return formatted;
  }
  return String(value);
}
